import { EditorSelection } from "@codemirror/state";
import { getIndentUnit, IndentContext, indentString, syntaxTree } from "@codemirror/language";

import {
    ancestorNodeOfType,
    ancestorOfNodeWithType,
    ancestorWithType,
    descendantsOfNodeWithType,
    wrappedNodeOfType,
} from "../utils/tree-operations/ancestors.js";
import { getEnvironmentName } from "../utils/tree-operations/environments.js";

export const ancestorListType = (state) => {
    const ancestorNode = ancestorWithType(state, "ListEnvironment");
    if (!ancestorNode) {
        return null;
    }
    return getEnvironmentName(ancestorNode, state);
};

const wrapRangeInList = (state, range, environment, prefix = "") => {
    const context = new IndentContext(state);
    const columns = context.lineIndent(range.from);
    const unit = getIndentUnit(state);
    const indent = indentString(state, columns);
    const itemIndent = indentString(state, columns + unit);

    const fromLine = state.doc.lineAt(range.from);
    const toLine = state.doc.lineAt(range.to);

    const lines = [`${indent}\\begin{${environment}}`];
    for (const line of state.doc.iterLines(fromLine.number, toLine.number + 1)) {
        let content = line.trim();
        if (content.endsWith("\\item")) {
            content += " ";
        }
        lines.push(`${itemIndent}${prefix}${content}`);
    }

    if (lines.length === 1) {
        lines.push(`${itemIndent}${prefix}`);
    }

    const changes = [
        {
            from: fromLine.from,
            to: toLine.to,
            insert: lines.join("\n"),
        },
    ];

    range = EditorSelection.cursor(range.to, -1).map(state.changes(changes), 1);

    changes.push({
        from: toLine.to,
        to: toLine.to,
        insert: `\n${indent}\\end{${environment}}`,
    });

    return {
        range,
        changes,
    };
};

const wrapRangesInList = (environment) => (view) => {
    view.dispatch(
        view.state.changeByRange((range) => wrapRangeInList(view.state, range, environment)),
        { scrollIntoView: true },
    );
    return true;
};

const unwrapRangeFromList = (state, range, environment) => {
    const node = syntaxTree(state).resolveInner(range.from);
    const list = ancestorOfNodeWithType(node, "ListEnvironment");
    if (!list) {
        return { range };
    }

    const fromLine = state.doc.lineAt(range.from);
    const toLine = state.doc.lineAt(range.to);

    const listFromLine = state.doc.lineAt(list.from);
    const listToLine = state.doc.lineAt(list.to);

    const context = new IndentContext(state);
    const columns = context.lineIndent(range.from);
    const unit = getIndentUnit(state);
    const indent = indentString(state, columns - unit);
    const changes = [];

    if (listFromLine.number === fromLine.number - 1) {
        changes.push({
            from: listFromLine.from,
            to: listFromLine.to + 1,
            insert: "",
        });
    } else {
        changes.push({
            from: fromLine.from,
            insert: `${indent}\\end{${environment}}\n`,
        });
    }

    const ensureSpace = (from, to) => /^\s*$/.test(state.doc.sliceString(from, to));

    for (let lineNumber = fromLine.number; lineNumber <= toLine.number; lineNumber += 1) {
        const line = state.doc.line(lineNumber);
        const to = line.from + unit;

        if (to <= line.to && ensureSpace(line.from, to)) {
            changes.push({
                from: line.from,
                to,
                insert: "",
            });
        }
    }

    if (listToLine.number === toLine.number + 1) {
        changes.push({
            from: listToLine.from,
            to: listToLine.to + 1,
            insert: "",
        });
    } else {
        changes.push({
            from: toLine.to,
            insert: `\n${indent}\\begin{${environment}}`,
        });
    }

    range = range.map(state.changes(changes), -1);
    return { range, changes };
};

const unwrapRangesFromList = (environment) => (view) => {
    view.dispatch(
        view.state.changeByRange((range) => unwrapRangeFromList(view.state, range, environment)),
        { scrollIntoView: true },
    );
    return true;
};

const toggleListForRange = (view, range, environment) => {
    const ancestorNode =
        ancestorNodeOfType(view.state, range.head, "ListEnvironment") ??
        wrappedNodeOfType(view.state, range, "ListEnvironment");

    if (!ancestorNode) {
        return wrapRangeInList(view.state, range, environment, "\\item ");
    }

    const beginEnvNode = ancestorNode.getChild("BeginEnv");
    const endEnvNode = ancestorNode.getChild("EndEnv");

    if (!beginEnvNode || !endEnvNode) {
        return { range };
    }

    const beginEnvNameNode = beginEnvNode?.getChild("EnvNameGroup")?.getChild("ListEnvName");
    const endEnvNameNode = endEnvNode?.getChild("EnvNameGroup")?.getChild("ListEnvName");

    if (!beginEnvNameNode || !endEnvNameNode) {
        return { range };
    }

    const envName = view.state.sliceDoc(beginEnvNameNode.from, beginEnvNameNode.to).trim();

    if (envName !== environment) {
        const changeSpec = [
            {
                from: beginEnvNameNode.from,
                to: beginEnvNameNode.to,
                insert: environment,
            },
            {
                from: endEnvNameNode.from,
                to: endEnvNameNode.to,
                insert: environment,
            },
        ];

        const changes = view.state.changes(changeSpec);
        return {
            range: range.map(changes),
            changes,
        };
    }

    const beginLine = view.state.doc.lineAt(beginEnvNode.from);
    const endLine = view.state.doc.lineAt(endEnvNode.from);
    const emptyBeginLine = /^\s*\\begin\{[^}]*}\s*$/.test(beginLine.text);
    const emptyEndLine = /^\s*\\end\{[^}]*}\s*$/.test(endLine.text);
    const changeSpec = [
        {
            from: emptyBeginLine ? beginLine.from : beginEnvNode.from,
            to: emptyBeginLine ? Math.min(beginLine.to + 1, view.state.doc.length) : beginEnvNode.to,
            insert: "",
        },
        {
            from: emptyEndLine ? Math.max(endLine.from - 1, 0) : endEnvNode.from,
            to: emptyEndLine ? endLine.to : endEnvNode.to,
            insert: "",
        },
    ];

    const itemNodes = descendantsOfNodeWithType(ancestorNode, "Item", "ListEnvironment");
    if (itemNodes.length > 0) {
        const indentUnit = getIndentUnit(view.state);

        for (const itemNode of itemNodes) {
            const change = {
                from: itemNode.from,
                to: itemNode.to,
                insert: "",
            };

            const line = view.state.doc.lineAt(itemNode.from);
            const lineBeforeCommand = view.state.sliceDoc(line.from, itemNode.from);
            if (lineBeforeCommand.trim().length === 0) {
                const context = new IndentContext(view.state);
                const indentation = context.lineIndent(itemNode.from);
                change.from -= Math.min(indentation ?? 0, indentUnit);
            }

            changeSpec.push(change);
        }
    }

    const changes = view.state.changes(changeSpec);
    return {
        range: range.map(changes),
        changes,
    };
};

export const getListItems = (node) => {
    const items = [];

    node.cursor().iterate((nodeRef) => {
        if (nodeRef.type.is("Item")) {
            items.push(nodeRef.node);
        }
        if (nodeRef.type.is("ListEnvironment") && nodeRef.node !== node) {
            return false;
        }
        return undefined;
    });

    return items;
};

export const toggleListForRanges = (environment) => (view) => {
    view.dispatch(
        view.state.changeByRange((range) => toggleListForRange(view, range, environment)),
        { scrollIntoView: true },
    );
    return true;
};

export const wrapInBulletList = wrapRangesInList("itemize");
export const wrapInNumberedList = wrapRangesInList("enumerate");
export const wrapInDescriptionList = wrapRangesInList("description");
export const unwrapBulletList = unwrapRangesFromList("itemize");
export const unwrapNumberedList = unwrapRangesFromList("enumerate");
export const unwrapDescriptionList = unwrapRangesFromList("description");
